import { useState, useEffect, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import Navigation from "../components/Navigation";
import VideoUpload from "../components/VideoUpload";
import { Card, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import {
  Play, Video, Headphones, Eye, CheckCircle, Clock, Upload as UploadIcon,
  Search, X, History, Film, Edit2, Trash2, Sparkles, ChevronRight,
  Brain, Moon, Zap, Target, TrendingUp, ListVideo, Plus,
  Crosshair, Shield, MessageSquare, Users, HeartPulse, Flame,
  Trophy, BookOpen, Volume2, Mic, Crown
} from "lucide-react";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import FavoriteButton from "../components/FavoriteButton";
import { PlaylistCard, PlaylistDetail, PlaylistEditor } from "../components/PlaylistComponents";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  'Authorization': `Bearer ${localStorage.getItem('token')}`
});

const CATEGORY_META = {
  'all': { label: 'All Videos', icon: Film, color: 'from-gray-600 to-gray-700', bg: 'bg-gray-50 border-gray-200 text-gray-700', activeBg: 'bg-gray-900 text-white' },
  'pre-game': { label: 'Pre-Game', icon: BookOpen, color: 'from-teal-500 to-emerald-600', bg: 'bg-teal-50 border-teal-200 text-teal-700', activeBg: 'bg-teal-600 text-white' },
  'visualization': { label: 'Visualization', icon: Eye, color: 'from-violet-500 to-purple-600', bg: 'bg-violet-50 border-violet-200 text-violet-700', activeBg: 'bg-violet-600 text-white' },
  'breathing': { label: 'Breathing', icon: Volume2, color: 'from-sky-500 to-blue-500', bg: 'bg-sky-50 border-sky-200 text-sky-700', activeBg: 'bg-sky-600 text-white' },
  'performance-anxiety': { label: 'Performance Anxiety', icon: Zap, color: 'from-yellow-500 to-amber-600', bg: 'bg-yellow-50 border-yellow-200 text-yellow-700', activeBg: 'bg-yellow-600 text-white' },
  'confidence': { label: 'Confidence', icon: Trophy, color: 'from-amber-500 to-yellow-600', bg: 'bg-amber-50 border-amber-200 text-amber-700', activeBg: 'bg-amber-600 text-white' },
  'self-talk': { label: 'Self-Talk', icon: MessageSquare, color: 'from-blue-500 to-cyan-600', bg: 'bg-blue-50 border-blue-200 text-blue-700', activeBg: 'bg-blue-600 text-white' },
  'goal-setting': { label: 'Goal Setting', icon: Target, color: 'from-emerald-500 to-teal-600', bg: 'bg-emerald-50 border-emerald-200 text-emerald-700', activeBg: 'bg-emerald-600 text-white' },
  'emotional-awareness': { label: 'Emotional Awareness', icon: HeartPulse, color: 'from-pink-500 to-rose-600', bg: 'bg-pink-50 border-pink-200 text-pink-700', activeBg: 'bg-pink-600 text-white' },
  'mistake-management': { label: 'Mistake Mgmt', icon: Shield, color: 'from-slate-500 to-gray-600', bg: 'bg-slate-50 border-slate-200 text-slate-700', activeBg: 'bg-slate-600 text-white' },
  'resilience': { label: 'Resilience', icon: Shield, color: 'from-rose-500 to-pink-600', bg: 'bg-rose-50 border-rose-200 text-rose-700', activeBg: 'bg-rose-600 text-white' },
  'leadership': { label: 'Leadership', icon: Crown, color: 'from-purple-500 to-indigo-600', bg: 'bg-purple-50 border-purple-200 text-purple-700', activeBg: 'bg-purple-600 text-white' },
  'team-cohesion': { label: 'Team Cohesion', icon: Users, color: 'from-green-500 to-emerald-600', bg: 'bg-green-50 border-green-200 text-green-700', activeBg: 'bg-green-600 text-white' },
  'team-dynamics': { label: 'Team Dynamics', icon: Users, color: 'from-indigo-500 to-blue-600', bg: 'bg-indigo-50 border-indigo-200 text-indigo-700', activeBg: 'bg-indigo-600 text-white' },
  'focus': { label: 'Focus', icon: Crosshair, color: 'from-cyan-500 to-blue-600', bg: 'bg-cyan-50 border-cyan-200 text-cyan-700', activeBg: 'bg-cyan-600 text-white' },
  'stress-management': { label: 'Stress Mgmt', icon: Moon, color: 'from-indigo-400 to-purple-500', bg: 'bg-indigo-50 border-indigo-200 text-indigo-700', activeBg: 'bg-indigo-500 text-white' },
  'arousal-regulation': { label: 'Energy Mgmt', icon: Flame, color: 'from-orange-500 to-red-500', bg: 'bg-orange-50 border-orange-200 text-orange-700', activeBg: 'bg-orange-600 text-white' },
  'performance-enhancement': { label: 'Performance', icon: TrendingUp, color: 'from-lime-500 to-green-600', bg: 'bg-lime-50 border-lime-200 text-lime-700', activeBg: 'bg-lime-600 text-white' },
  'motivation': { label: 'Motivation', icon: Sparkles, color: 'from-fuchsia-500 to-pink-600', bg: 'bg-fuchsia-50 border-fuchsia-200 text-fuchsia-700', activeBg: 'bg-fuchsia-600 text-white' },
  'recovery': { label: 'Recovery', icon: HeartPulse, color: 'from-red-500 to-rose-600', bg: 'bg-red-50 border-red-200 text-red-700', activeBg: 'bg-red-600 text-white' },
  'reframing': { label: 'Reframing', icon: Brain, color: 'from-teal-400 to-cyan-500', bg: 'bg-teal-50 border-teal-200 text-teal-700', activeBg: 'bg-teal-500 text-white' },
};

const AASP_CATEGORIES = Object.keys(CATEGORY_META);

export default function VideoLibrary({ user, onLogout, embedded = false }) {
  const [videos, setVideos] = useState([]);
  const [coachVideos, setCoachVideos] = useState([]);
  const [myUploads, setMyUploads] = useState([]);
  const [watchHistory, setWatchHistory] = useState([]);
  const [stats, setStats] = useState(null);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [editingVideo, setEditingVideo] = useState(null);
  const [playlists, setPlaylists] = useState([]);
  const [activePlaylistId, setActivePlaylistId] = useState(null);
  const [showPlaylistEditor, setShowPlaylistEditor] = useState(false);
  const [editingPlaylist, setEditingPlaylist] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedSport, setSelectedSport] = useState("all");
  const [activeTab, setActiveTab] = useState("library");
  const [searchQuery, setSearchQuery] = useState("");
  const videoRef = useRef(null);
  const progressInterval = useRef(null);
  const [searchParams] = useSearchParams();

  const isCoachOrAdmin = user && ['coach', 'sport-performance-consultant', 'system-admin'].includes(user.role);

  useEffect(() => {
    fetchVideos();
    fetchCoachVideos();
    fetchWatchHistory();
    fetchStats();
    fetchPlaylists();
    if (isCoachOrAdmin) fetchMyUploads();
  }, [selectedCategory, selectedSport]);

  // Read search query from URL params (e.g., /videos?search=Visualization)
  useEffect(() => {
    const searchFromUrl = searchParams.get('search');
    if (searchFromUrl) {
      setSearchQuery(decodeURIComponent(searchFromUrl));
    }
  }, [searchParams]);

  useEffect(() => {
    return () => {
      if (progressInterval.current) clearInterval(progressInterval.current);
    };
  }, []);

  const fetchVideos = async () => {
    try {
      let url = `${API}/videos`;
      const params = [];
      if (selectedCategory !== "all") params.push(`category=${selectedCategory}`);
      if (selectedSport !== "all") params.push(`sport=${selectedSport}`);
      if (params.length > 0) url += "?" + params.join("&");
      const response = await axios.get(url);
      setVideos(response.data);
    } catch (error) {
      console.error("Failed to load videos");
    }
  };

  const fetchCoachVideos = async () => {
    try {
      let url = `${API}/videos/coach-uploads`;
      const params = [];
      if (selectedCategory !== "all" && selectedCategory !== "sport-psychology") {
        params.push(`category=${selectedCategory}`);
      }
      if (params.length > 0) url += "?" + params.join("&");
      const response = await axios.get(url, { headers: getAuthHeaders() });
      setCoachVideos(response.data);
    } catch (error) {
      console.error("Failed to load coach videos");
    }
  };

  const fetchMyUploads = async () => {
    try {
      const response = await axios.get(`${API}/videos/my-uploads`, { headers: getAuthHeaders() });
      setMyUploads(response.data);
    } catch (error) {
      console.error("Failed to load my uploads");
    }
  };

  const fetchWatchHistory = async () => {
    try {
      const response = await axios.get(`${API}/videos/watch-history`, { headers: getAuthHeaders() });
      setWatchHistory(response.data);
    } catch (error) {
      console.error("Failed to load watch history");
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/videos/stats`, { headers: getAuthHeaders() });
      setStats(response.data);
    } catch (error) {
      console.error("Failed to load stats");
    }
  };

  const fetchPlaylists = async () => {
    try {
      const response = await axios.get(`${API}/videos/playlists`, { headers: getAuthHeaders() });
      setPlaylists(response.data);
    } catch (error) {
      console.error("Failed to load playlists");
    }
  };

  const handleDeletePlaylist = async (playlistId) => {
    if (!window.confirm("Delete this playlist?")) return;
    try {
      await axios.delete(`${API}/videos/playlists/${playlistId}`, { headers: getAuthHeaders() });
      toast.success("Playlist deleted!");
      fetchPlaylists();
      setActivePlaylistId(null);
    } catch (error) {
      toast.error("Failed to delete playlist");
    }
  };

  const getVideoEmbedUrl = (url) => {
    if (!url) return "";
    if (url.includes("youtube.com") || url.includes("youtu.be")) {
      const videoId = url.includes("v=")
        ? url.split("v=")[1]?.split("&")[0]
        : url.split("/").pop()?.split("?")[0];
      return `https://www.youtube.com/embed/${videoId}`;
    }
    if (url.includes("vimeo.com")) {
      const videoId = url.split("/").pop();
      return `https://player.vimeo.com/video/${videoId}`;
    }
    return url;
  };

  const isDirectFile = (video) => {
    return video.upload_type === "file" || video.file_url?.includes("/api/videos/stream/");
  };

  const getStreamUrl = (video) => {
    if (video.file_url) {
      if (video.file_url.startsWith("/")) return `${BACKEND_URL}${video.file_url}`;
      return video.file_url;
    }
    return video.video_url || video.url || "";
  };

  const getDifficultyColor = (difficulty) => {
    const colors = {
      beginner: "bg-emerald-500/90 text-white",
      intermediate: "bg-amber-500/90 text-white",
      advanced: "bg-red-500/90 text-white"
    };
    return colors[difficulty] || colors.beginner;
  };

  const handleVideoUploaded = (newVideo) => {
    setMyUploads(prev => [newVideo, ...prev]);
    if (newVideo.approved) fetchCoachVideos();
    fetchStats();
  };

  const handleVideoView = async (videoId, isCoachVideo = false) => {
    if (isCoachVideo) {
      try {
        await axios.patch(`${API}/videos/${videoId}/view`, {}, { headers: getAuthHeaders() });
      } catch (error) {
        console.error("Failed to update view count");
      }
    }
  };

  const saveWatchProgress = useCallback(async (videoId, videoType, seconds, completed = false) => {
    try {
      await axios.post(`${API}/videos/watch-history`, {
        video_id: videoId,
        video_type: videoType,
        progress_seconds: Math.floor(seconds),
        completed
      }, { headers: getAuthHeaders() });
    } catch (error) { console.error("Video playback error:", error); }
  }, []);

  const startProgressTracking = useCallback((videoId, videoType) => {
    if (progressInterval.current) clearInterval(progressInterval.current);
    progressInterval.current = setInterval(() => {
      if (videoRef.current) {
        const currentTime = videoRef.current.currentTime;
        const duration = videoRef.current.duration;
        const completed = duration > 0 && currentTime >= duration - 2;
        saveWatchProgress(videoId, videoType, currentTime, completed);
      }
    }, 10000);
  }, [saveWatchProgress]);

  const handleOpenVideo = (video, isCoachVideo) => {
    setSelectedVideo({ ...video, isCoachVideo });
    if (isCoachVideo) handleVideoView(video.id, true);
    const historyEntry = watchHistory.find(h => h.video_id === video.id);
    if (historyEntry && isDirectFile(video)) {
      setTimeout(() => {
        if (videoRef.current && historyEntry.progress_seconds > 0) {
          videoRef.current.currentTime = historyEntry.progress_seconds;
        }
      }, 500);
    }
    const videoType = isCoachVideo ? "coach" : "library";
    if (isDirectFile(video)) {
      setTimeout(() => startProgressTracking(video.id, videoType), 500);
    } else {
      saveWatchProgress(video.id, videoType, 0, false);
    }
  };

  const handleCloseVideo = () => {
    if (progressInterval.current) clearInterval(progressInterval.current);
    if (videoRef.current && selectedVideo) {
      const videoType = selectedVideo.isCoachVideo ? "coach" : "library";
      const currentTime = videoRef.current.currentTime;
      const duration = videoRef.current.duration;
      saveWatchProgress(selectedVideo.id, videoType, currentTime, duration > 0 && currentTime >= duration - 2);
    }
    setSelectedVideo(null);
    fetchWatchHistory();
  };

  const handleApproveVideo = async (videoId) => {
    try {
      await axios.patch(`${API}/videos/${videoId}/approve`, {}, { headers: getAuthHeaders() });
      toast.success("Video approved!");
      fetchMyUploads();
      fetchCoachVideos();
    } catch (error) {
      toast.error("Failed to approve video");
    }
  };

  const handleDeleteVideo = async (videoId) => {
    if (!window.confirm("Delete this video?")) return;
    try {
      await axios.delete(`${API}/videos/${videoId}`, { headers: getAuthHeaders() });
      toast.success("Video deleted!");
      setMyUploads(prev => prev.filter(v => v.id !== videoId));
      fetchCoachVideos();
      fetchStats();
    } catch (error) {
      toast.error("Failed to delete video");
    }
  };

  const handleEditVideo = async (e) => {
    e.preventDefault();
    if (!editingVideo) return;
    try {
      await axios.put(`${API}/videos/${editingVideo.id}/edit`, {
        title: editingVideo.title,
        description: editingVideo.description,
        category: editingVideo.category,
        tags: editingVideo.tags,
        duration_minutes: editingVideo.duration_minutes
      }, { headers: getAuthHeaders() });
      toast.success("Video updated!");
      setEditingVideo(null);
      fetchMyUploads();
      fetchCoachVideos();
    } catch (error) {
      toast.error("Failed to update video");
    }
  };

  const filterVideos = (videoList) => {
    if (!searchQuery.trim()) return videoList;
    const query = searchQuery.toLowerCase();
    const keywords = query.split(/[\s-]+/).filter(k => k.length > 2);
    if (keywords.length === 0) return videoList;
    
    // Score each video — more keyword hits = higher rank
    const scored = videoList.map(video => {
      const searchable = [
        video.title, video.description, video.category?.replace(/-/g, ' '),
        video.sport, video.instructor, video.uploaded_by_name,
        ...(video.tags || [])
      ].filter(Boolean).join(' ').toLowerCase();
      let score = 0;
      for (const k of keywords) {
        if (searchable.includes(k)) score++;
      }
      return { video, score };
    });
    
    // Return videos with at least 1 keyword match, sorted by score descending
    return scored
      .filter(s => s.score > 0)
      .sort((a, b) => b.score - a.score)
      .map(s => s.video);
  };

  const getCategoryLabel = (cat) => CATEGORY_META[cat]?.label || cat.replace(/-/g, ' ');

  const renderVideoCard = (video, isCoachVideo = false, isMyUpload = false) => {
    const catMeta = CATEGORY_META[video.category] || CATEGORY_META['all'];
    const historyEntry = watchHistory.find(h => h.video_id === video.id);
    const isAudio = video.content_type === 'audio' || video.file_type === 'audio';

    return (
      <div
        key={video.id}
        data-testid={`video-card-${video.id}`}
        onClick={() => handleOpenVideo(video, isCoachVideo)}
        className="group cursor-pointer rounded-xl overflow-hidden bg-white border border-gray-100 hover:border-gray-200 shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-0.5"
      >
        {/* Thumbnail */}
        <div className="relative aspect-video overflow-hidden bg-gray-900">
          <img
            src={video.thumbnail || video.thumbnail_url || `https://placehold.co/400x225/1a1a2e/eaeaea?text=${encodeURIComponent(video.title?.substring(0, 20) || 'Video')}`}
            alt={video.title}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            onError={(e) => { e.target.src = `https://placehold.co/400x225/1a1a2e/eaeaea?text=Video`; }}
          />
          {/* Play overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center opacity-80 group-hover:opacity-100 group-hover:scale-110 transition-all duration-300">
              {isAudio ? (
                <Headphones className="w-5 h-5 text-white" />
              ) : (
                <Play className="w-5 h-5 text-white ml-0.5" />
              )}
            </div>
          </div>

          {/* Top badges */}
          <div className="absolute top-2.5 left-2.5 flex gap-1.5">
            {video.difficulty && (
              <span className={`px-2 py-0.5 rounded-md text-[10px] font-semibold tracking-wide uppercase ${getDifficultyColor(video.difficulty)}`}>
                {video.difficulty}
              </span>
            )}
            {isAudio && (
              <span className="px-2 py-0.5 rounded-md text-[10px] font-semibold bg-violet-500/90 text-white flex items-center gap-1">
                <Volume2 className="w-2.5 h-2.5" /> Audio
              </span>
            )}
          </div>

          {/* Duration badge */}
          <div className="absolute bottom-2.5 right-2.5">
            <span className="px-2 py-0.5 rounded-md text-[11px] font-medium bg-black/70 text-white backdrop-blur-sm">
              {video.duration_minutes || video.duration || '—'} min
            </span>
          </div>

          {/* Coach/File badges */}
          <div className="absolute top-2.5 right-2.5 flex flex-col gap-1">
            {isCoachVideo && (
              <span className="px-2 py-0.5 rounded-md text-[10px] font-semibold bg-blue-500/90 text-white">COACH</span>
            )}
            {video.upload_type === 'file' && (
              <span className="px-2 py-0.5 rounded-md text-[10px] font-semibold bg-violet-500/90 text-white flex items-center gap-1">
                <Mic className="w-2.5 h-2.5" /> FILE
              </span>
            )}
            {isMyUpload && (
              <span className={`px-2 py-0.5 rounded-md text-[10px] font-semibold ${video.approved ? 'bg-emerald-500/90 text-white' : 'bg-amber-500/90 text-white'}`}>
                {video.approved ? 'APPROVED' : 'PENDING'}
              </span>
            )}
          </div>

          {/* Watch progress bar */}
          {historyEntry && (
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/20">
              <div
                className="h-full bg-emerald-400 transition-all"
                style={{
                  width: (() => {
                    if (historyEntry.completed) return '100%';
                    const dur = (video.duration_minutes || video.duration || 10) * 60;
                    return `${Math.min(100, (historyEntry.progress_seconds / dur) * 100)}%`;
                  })()
                }}
              />
            </div>
          )}

          {/* View count for coach videos */}
          {isCoachVideo && (
            <div className="absolute bottom-2.5 left-2.5">
              <span className="bg-black/60 text-white px-2 py-0.5 rounded-md text-[10px] flex items-center gap-1 backdrop-blur-sm">
                <Eye className="w-2.5 h-2.5" /> {video.views_count || 0}
              </span>
            </div>
          )}
        </div>

        {/* Card body */}
        <div className="p-3.5">
          <div className="flex items-center gap-1.5 mb-1.5">
            {catMeta.icon && <catMeta.icon className="w-3 h-3 text-gray-400 flex-shrink-0" />}
            <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">
              {getCategoryLabel(video.category)}
            </span>
          </div>
          <h3 className="text-sm font-semibold text-gray-900 leading-snug line-clamp-2 mb-1.5">{video.title}</h3>
          <div className="flex items-center justify-between">
            <p className="text-xs text-gray-500 truncate">{video.instructor || video.uploaded_by_name || 'Unknown'}</p>
            <div onClick={(e) => e.stopPropagation()}>
              <FavoriteButton
                itemType={isAudio ? 'audio' : 'video'}
                itemId={video.id}
                itemTitle={video.title}
                itemSource="video_library"
                itemData={{ duration: video.duration_minutes || video.duration, category: video.category, instructor: video.instructor || video.uploaded_by_name }}
                size="sm"
              />
            </div>
          </div>

          {isMyUpload && (
            <div className="flex gap-2 mt-3 pt-3 border-t border-gray-100" onClick={(e) => e.stopPropagation()}>
              <button
                data-testid={`edit-video-${video.id}`}
                onClick={() => setEditingVideo({ ...video, tags: video.tags || [] })}
                className="flex items-center gap-1 bg-blue-50 hover:bg-blue-100 text-blue-700 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors"
              >
                <Edit2 className="w-3 h-3" /> Edit
              </button>
              {!video.approved && user.role === 'system-admin' && (
                <button
                  data-testid={`approve-video-${video.id}`}
                  onClick={() => handleApproveVideo(video.id)}
                  className="flex items-center gap-1 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors"
                >
                  <CheckCircle className="w-3 h-3" /> Approve
                </button>
              )}
              <button
                data-testid={`delete-video-${video.id}`}
                onClick={() => handleDeleteVideo(video.id)}
                className="flex items-center gap-1 bg-red-50 hover:bg-red-100 text-red-700 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors"
              >
                <Trash2 className="w-3 h-3" /> Delete
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderWatchHistoryCard = (entry) => (
    <div
      key={entry.video_id}
      data-testid={`history-card-${entry.video_id}`}
      className="flex items-center gap-4 p-3.5 bg-white rounded-xl border border-gray-100 hover:border-gray-200 hover:shadow-md cursor-pointer transition-all duration-200"
      onClick={() => {
        const video = [...videos, ...coachVideos].find(v => v.id === entry.video_id);
        if (video) handleOpenVideo(video, entry.video_type === "coach");
      }}
    >
      <div className="w-24 h-16 rounded-lg overflow-hidden flex-shrink-0 bg-gray-100 flex items-center justify-center relative">
        {entry.video_thumbnail ? (
          <img src={entry.video_thumbnail} alt="" className="w-full h-full object-cover" />
        ) : (
          <Film className="w-6 h-6 text-gray-300" />
        )}
        <div className="absolute inset-0 flex items-center justify-center bg-black/10">
          <Play className="w-5 h-5 text-white/90" />
        </div>
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900 truncate">{entry.video_title}</p>
        <div className="flex items-center gap-3 mt-1">
          <span className="text-xs text-gray-400">{getCategoryLabel(entry.video_category) || 'Video'}</span>
          {entry.completed && (
            <span className="text-xs text-emerald-600 flex items-center gap-0.5 font-medium">
              <CheckCircle className="w-3 h-3" /> Completed
            </span>
          )}
          {!entry.completed && entry.progress_seconds > 0 && (
            <span className="text-xs text-blue-600 font-medium">
              {Math.floor(entry.progress_seconds / 60)}m watched
            </span>
          )}
        </div>
      </div>
      <span className="text-xs text-gray-300 flex-shrink-0">
        {new Date(entry.last_watched).toLocaleDateString()}
      </span>
    </div>
  );

  // When embedded, skip the outer shell (Navigation, bg, padding)
  const content = (
    <>
      <div className={embedded ? "" : "max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8"}>
        {/* Header - only in standalone mode */}
        {!embedded && (
          <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 mb-4 sm:mb-8">
            <div>
              <h1 data-testid="video-library-title" className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 tracking-tight">
                Video Library
              </h1>
              <p className="text-sm text-gray-500 mt-1">
                AASP-aligned sport psychology content and custom coach resources
              </p>
            </div>
            <VideoUpload user={user} onVideoUploaded={handleVideoUploaded} />
          </div>
        )}

        {/* Stats Row */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
            {[
              { label: "Library Videos", value: stats.total_library_videos, icon: Film, gradient: "from-blue-500 to-blue-600" },
              { label: "Coach Content", value: stats.total_coach_videos, icon: UploadIcon, gradient: "from-violet-500 to-violet-600" },
              { label: "Watched", value: stats.videos_watched, icon: Eye, gradient: "from-amber-500 to-amber-600" },
              { label: "Completed", value: stats.videos_completed, icon: CheckCircle, gradient: "from-emerald-500 to-emerald-600" },
            ].map((s, i) => (
              <div key={i} data-testid={`stat-${s.label.toLowerCase().replace(' ', '-')}`} className="bg-white rounded-xl p-4 border border-gray-100 flex items-center gap-3">
                <div className={`p-2.5 rounded-lg bg-gradient-to-br ${s.gradient} shadow-sm`}>
                  <s.icon className="w-4 h-4 text-white" />
                </div>
                <div>
                  <p className="text-xl font-bold text-gray-900">{s.value}</p>
                  <p className="text-xs text-gray-400 font-medium">{s.label}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Search */}
        <div className="mb-6">
          <div className="relative max-w-xl">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-300" />
            <input
              data-testid="video-search-input"
              type="text"
              placeholder="Search videos, instructors, topics..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-11 pr-10 py-3 text-sm bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500/40 focus:border-emerald-400 transition-all placeholder:text-gray-300"
            />
            {searchQuery && (
              <button onClick={() => setSearchQuery("")} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-300 hover:text-gray-500 transition-colors">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {/* Main Tabs */}
        <div className="mb-6">
          <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); setActivePlaylistId(null); }}>
            <TabsList className={`grid w-full max-w-2xl ${isCoachOrAdmin ? 'grid-cols-5' : 'grid-cols-4'} gap-1`}>
              <TabsTrigger data-testid="tab-library" value="library">Library</TabsTrigger>
              <TabsTrigger data-testid="tab-playlists" value="playlists">
                <ListVideo className="w-3.5 h-3.5 mr-1" /> Paths
              </TabsTrigger>
              <TabsTrigger data-testid="tab-coach-content" value="coach-content">Coach</TabsTrigger>
              <TabsTrigger data-testid="tab-history" value="history">
                <History className="w-3.5 h-3.5 mr-1" /> History
              </TabsTrigger>
              {isCoachOrAdmin && (
                <TabsTrigger data-testid="tab-my-uploads" value="my-uploads">My Uploads</TabsTrigger>
              )}
            </TabsList>
          </Tabs>
        </div>

        {/* Category Filter with Icons */}
        {(activeTab === 'library' || activeTab === 'coach-content') && (
          <div className="mb-8">
            <div className="flex flex-wrap gap-2">
              {AASP_CATEGORIES.map(cat => {
                const meta = CATEGORY_META[cat];
                const Icon = meta.icon;
                const isActive = selectedCategory === cat;
                return (
                  <button
                    key={cat}
                    data-testid={`cat-${cat}`}
                    onClick={() => setSelectedCategory(cat)}
                    className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-semibold border transition-all duration-200 ${
                      isActive
                        ? `${meta.activeBg} border-transparent shadow-sm`
                        : `${meta.bg} border hover:shadow-sm`
                    }`}
                  >
                    <Icon className="w-3.5 h-3.5" />
                    {meta.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Content */}
        {activeTab === 'library' && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {filterVideos(videos).map(video => renderVideoCard(video))}
            </div>
            {filterVideos(videos).length === 0 && (
              <div className="text-center py-20">
                <Video className="w-12 h-12 text-gray-200 mx-auto mb-3" />
                <p className="text-gray-400 text-sm font-medium">
                  {searchQuery ? `No videos found for "${searchQuery}"` : "No videos in this category"}
                </p>
              </div>
            )}
          </>
        )}

        {/* Playlists / Learning Paths */}
        {activeTab === 'playlists' && (
          <div>
            {activePlaylistId ? (
              <PlaylistDetail
                playlistId={activePlaylistId}
                onBack={() => setActivePlaylistId(null)}
                onPlayVideo={(video, idx) => handleOpenVideo(video, video.video_type === 'coach')}
                user={user}
              />
            ) : (
              <>
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-lg font-bold text-gray-900">Learning Paths</h2>
                    <p className="text-sm text-gray-400">Curated video sequences for structured training</p>
                  </div>
                  {isCoachOrAdmin && (
                    <button
                      data-testid="create-playlist-btn"
                      onClick={() => { setEditingPlaylist(null); setShowPlaylistEditor(true); }}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2.5 rounded-xl flex items-center gap-2 text-sm font-semibold transition-colors shadow-sm"
                    >
                      <Plus className="w-4 h-4" /> Create Path
                    </button>
                  )}
                </div>
                {playlists.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                    {playlists.map(pl => (
                      <div key={pl.id} className="relative">
                        <PlaylistCard playlist={pl} onClick={() => setActivePlaylistId(pl.id)} />
                        {isCoachOrAdmin && pl.created_by_id === user?.id && (
                          <div className="absolute top-3 right-3 flex gap-1 z-10" onClick={(e) => e.stopPropagation()}>
                            <button
                              data-testid={`edit-playlist-${pl.id}`}
                              onClick={() => { setEditingPlaylist(pl); setShowPlaylistEditor(true); }}
                              className="p-1.5 bg-white/90 rounded-lg shadow-sm text-blue-600 hover:bg-blue-50 transition-colors"
                            >
                              <Edit2 className="w-3 h-3" />
                            </button>
                            <button
                              data-testid={`delete-playlist-${pl.id}`}
                              onClick={() => handleDeletePlaylist(pl.id)}
                              className="p-1.5 bg-white/90 rounded-lg shadow-sm text-red-600 hover:bg-red-50 transition-colors"
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-20">
                    <ListVideo className="w-12 h-12 text-gray-200 mx-auto mb-3" />
                    <p className="text-gray-400 text-sm">No learning paths yet</p>
                    {isCoachOrAdmin && (
                      <button
                        onClick={() => { setEditingPlaylist(null); setShowPlaylistEditor(true); }}
                        className="mt-3 text-emerald-600 hover:text-emerald-700 text-sm font-semibold"
                      >
                        Create the first one
                      </button>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {activeTab === 'coach-content' && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {filterVideos(coachVideos).map(video => renderVideoCard(video, true))}
            </div>
            {filterVideos(coachVideos).length === 0 && (
              <div className="text-center py-20">
                <UploadIcon className="w-12 h-12 text-gray-200 mx-auto mb-3" />
                <p className="text-gray-400 text-sm font-medium">
                  {searchQuery ? `No coach videos for "${searchQuery}"` : "No coach videos yet"}
                </p>
              </div>
            )}
          </>
        )}

        {activeTab === 'history' && (
          <div>
            <h2 className="text-lg font-bold text-gray-900 mb-4">Recently Watched</h2>
            {watchHistory.length > 0 ? (
              <div className="space-y-2 max-w-3xl">
                {watchHistory.map(entry => renderWatchHistoryCard(entry))}
              </div>
            ) : (
              <div className="text-center py-20">
                <History className="w-12 h-12 text-gray-200 mx-auto mb-3" />
                <p className="text-gray-400 text-sm">No watch history yet. Start watching videos!</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'my-uploads' && isCoachOrAdmin && (
          <>
            <div className="mb-5">
              <h2 className="text-lg font-bold text-gray-900">My Uploads</h2>
              <p className="text-sm text-gray-400">Manage your uploaded content</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {filterVideos(myUploads).map(video => renderVideoCard(video, true, true))}
            </div>
            {filterVideos(myUploads).length === 0 && (
              <div className="text-center py-20">
                <UploadIcon className="w-12 h-12 text-gray-200 mx-auto mb-3" />
                <p className="text-gray-400 text-sm">
                  {searchQuery ? `No uploads for "${searchQuery}"` : "No uploads yet"}
                </p>
              </div>
            )}
          </>
        )}
      </div>

      {/* Video Player Dialog */}
      <Dialog open={!!selectedVideo} onOpenChange={handleCloseVideo}>
        <DialogContent data-testid="video-player-dialog" className="max-w-4xl p-0 overflow-hidden">
          <DialogHeader className="p-5 pb-0">
            <DialogTitle className="text-xl pr-8 font-bold">{selectedVideo?.title}</DialogTitle>
          </DialogHeader>
          {selectedVideo && (
            <div className="p-5 pt-3 space-y-4">
              {isDirectFile(selectedVideo) ? (
                selectedVideo.file_type === 'audio' || selectedVideo.content_type?.startsWith('audio') ? (
                  <div className="bg-gradient-to-br from-violet-50 to-indigo-50 p-8 rounded-xl text-center">
                    <Headphones className="w-14 h-14 text-violet-600 mx-auto mb-4" />
                    <audio ref={videoRef} controls className="w-full max-w-md mx-auto" src={getStreamUrl(selectedVideo)} />
                  </div>
                ) : (
                  <div className="aspect-video bg-black rounded-xl overflow-hidden">
                    <video ref={videoRef} controls className="w-full h-full" src={getStreamUrl(selectedVideo)} />
                  </div>
                )
              ) : (
                selectedVideo.content_type === 'audio' ? (
                  <div className="bg-gradient-to-br from-violet-50 to-indigo-50 p-8 rounded-xl text-center">
                    <Headphones className="w-14 h-14 text-violet-600 mx-auto mb-4" />
                    <audio ref={videoRef} controls className="w-full max-w-md mx-auto" preload="none">
                      <source src={selectedVideo.url || selectedVideo.video_url} type="audio/mpeg" />
                    </audio>
                  </div>
                ) : (
                  <div className="aspect-video rounded-xl overflow-hidden">
                    <iframe
                      width="100%"
                      height="100%"
                      src={getVideoEmbedUrl(selectedVideo.url || selectedVideo.video_url)}
                      title={selectedVideo.title}
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                )
              )}
              <div>
                <p className="text-sm text-gray-600 mb-3 leading-relaxed">{selectedVideo.description}</p>
                <div className="flex flex-wrap items-center gap-3 text-xs text-gray-400">
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {selectedVideo.duration_minutes || selectedVideo.duration || 'N/A'} min
                  </span>
                  <span className="font-medium text-gray-500">{selectedVideo.instructor || selectedVideo.uploaded_by_name || 'Unknown'}</span>
                  {selectedVideo.difficulty && (
                    <span className={`px-2 py-0.5 rounded-md text-[10px] font-semibold ${getDifficultyColor(selectedVideo.difficulty)}`}>
                      {selectedVideo.difficulty}
                    </span>
                  )}
                  {selectedVideo.isCoachVideo && (
                    <span className="flex items-center gap-1">
                      <Eye className="w-3 h-3" /> {selectedVideo.views_count || 0} views
                    </span>
                  )}
                </div>
                {selectedVideo.tags && selectedVideo.tags.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {selectedVideo.tags.map((tag, i) => (
                      <span key={i} className="px-2.5 py-1 bg-gray-50 text-gray-500 rounded-lg text-xs font-medium border border-gray-100">{tag}</span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Video Dialog */}
      <Dialog open={!!editingVideo} onOpenChange={() => setEditingVideo(null)}>
        <DialogContent data-testid="edit-video-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-bold">Edit Video</DialogTitle>
          </DialogHeader>
          {editingVideo && (
            <form onSubmit={handleEditVideo} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Title</label>
                <input
                  data-testid="edit-video-title"
                  type="text"
                  value={editingVideo.title}
                  onChange={(e) => setEditingVideo(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/40 focus:border-emerald-400"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Description</label>
                <textarea
                  data-testid="edit-video-description"
                  value={editingVideo.description}
                  onChange={(e) => setEditingVideo(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                  className="w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/40 focus:border-emerald-400"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Category</label>
                  <select
                    data-testid="edit-video-category"
                    value={editingVideo.category}
                    onChange={(e) => setEditingVideo(prev => ({ ...prev, category: e.target.value }))}
                    className="w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/40 focus:border-emerald-400"
                  >
                    {AASP_CATEGORIES.filter(c => c !== 'all').map(c => (
                      <option key={c} value={c}>{CATEGORY_META[c].label}</option>
                    ))}
                    <option value="custom">Custom</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Duration (min)</label>
                  <input
                    data-testid="edit-video-duration"
                    type="number"
                    value={editingVideo.duration_minutes || ''}
                    onChange={(e) => setEditingVideo(prev => ({ ...prev, duration_minutes: parseInt(e.target.value) || null }))}
                    className="w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/40 focus:border-emerald-400"
                    min="1"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Tags (comma separated)</label>
                <input
                  data-testid="edit-video-tags"
                  type="text"
                  value={Array.isArray(editingVideo.tags) ? editingVideo.tags.join(', ') : editingVideo.tags}
                  onChange={(e) => setEditingVideo(prev => ({ ...prev, tags: e.target.value.split(',').map(t => t.trim()).filter(Boolean) }))}
                  className="w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/40 focus:border-emerald-400"
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setEditingVideo(null)}
                  className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 py-2.5 rounded-xl text-sm font-semibold transition-colors"
                >
                  Cancel
                </button>
                <button
                  data-testid="save-edit-btn"
                  type="submit"
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white py-2.5 rounded-xl text-sm font-semibold transition-colors shadow-sm"
                >
                  Save Changes
                </button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Playlist Editor Dialog */}
      <PlaylistEditor
        open={showPlaylistEditor}
        onClose={() => { setShowPlaylistEditor(false); setEditingPlaylist(null); }}
        onSaved={fetchPlaylists}
        editPlaylist={editingPlaylist}
        allVideos={videos}
      />
    </>
  );

  if (embedded) return content;

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation user={user} onLogout={onLogout} />
      {content}
    </div>
  );
}
